import { store } from '../common/redux_store.js'
import * as Action from '../common/redux_actions.js'
import { database } from '../common/dexie_database.js'
import { objectAssignDeep, isArray, isObject, offlineModeIsActive } from '../common/helpers.js'
import { ajaxRequest } from '../common/ajax.js'
import { getCookie, newZuluDatetime } from '../common/helpers.js'
import { EditorState, ContentState, convertFromRaw } from 'draft-js'
import { v1 as uuidv1 } from 'uuid'

var global_athlete_id_to_keep;

/**
 * User account object
 */
export const User = function User(saved_values) {

  var self = this;

  // Set up default values.
  this.id = null;
  this.beta_tester_level = null;
  this.beta_features = {};

  this.set_saved_values = function (saved_values) {
    Object.keys(saved_values).forEach(key => {

      let value = saved_values[key];

      if (value instanceof Date) {
        // Make sure not to destroy dates and turn them into regular objects
        // which would happen if we copy them as a regular object.
        this[key] = value;
      }
      else if (key == 'coaching_start_datetime' && typeof(value) == 'string') {
        this[key] = newZuluDatetime(value);
      }
      else if (key == 'notes') {
        this.notes = {}
        this.insertOrUpdateNotes(value);
      }
      else if (isArray(value)) {
        self[key] = objectAssignDeep([], value);
      }
      else if (isObject(value)) {
        self[key] = objectAssignDeep({}, value);
      }
      else {
        self[key] = value;
      }

    });
  }

  this.set_saved_values(saved_values);

}

User.prototype.insertOrUpdateNotes = function (new_and_updated_notes, keep_existing_note) {

  let notes_with_temp_ids_to_delete = [];

  // Create a new object reresenting all of the existing notes
  // so that we can check that they are not equal.
  let notes = {};
  if (typeof(this.notes) != 'undefined') {
    Object.keys(this.notes).forEach(key => {
      let note_object = this.notes[key];
      notes[note_object.id] = note_object;
    });
  }

  // Merge in the updates.
  Object.keys(new_and_updated_notes).forEach(key => {

    let note_object = new_and_updated_notes[key];
    let note;
    if (!isNaN(note_object.id) && note_object.uuid && notes['uuid-' + note_object.uuid]) {
      // If there is a real ID, use the note that is in memory and delete
      // the reference of it that uses the temp ID as we can now use the
      // real ID.
      note = notes['uuid-' + note_object.uuid].note;
      notes_with_temp_ids_to_delete.push(note_object.uuid)
    }
    else if (keep_existing_note && notes[note_object.id]) {
      // Use the existing note rather than updating it. This is useful if the
      // note has been returned from the server but we want to keep the in
      // memory note.
      note = notes[note_object.id].note;
    }
    else {
      // EditorStates should remain as they are. Content from the server
      // should be converted into an EditorState.
      if (note_object.note instanceof EditorState) {
        note = note_object.note;
      }
      else {
        try {
          note = EditorState.createWithContent(convertFromRaw(note_object.note));
        }
        catch (error) {
          note = EditorState.createWithContent(ContentState.createFromText(note_object.note));
        }
      }
    }

    // Ensure a note has a uuid.
    if (!note_object.uuid) {
      note_object.uuid = uuidv1();
    }

    // Ensure dates have been converted, if they exist.
    if (typeof(note_object.datetime_added) == 'string') {
      note_object.datetime_added = newZuluDatetime(note_object.datetime_added);
    }
    if (typeof(note_object.datetime_updated) == 'string') {
      note_object.datetime_updated = newZuluDatetime(note_object.datetime_updated);
    }

    notes[note_object.id] = {
      id: note_object.id,
      uuid: note_object.uuid,
      note: note,
      datetime_added: note_object.datetime_added,
      datetime_updated: note_object.datetime_updated,
    };

  });

  for (let i = 0; i < notes_with_temp_ids_to_delete.length; i++) {
    delete notes['uuid-' + notes_with_temp_ids_to_delete[i]];
  }

  this.notes = notes;
}

User.prototype.deleteNote = function (note_id) {

  // Create a new object reresenting all of the existing notes
  // so that we can check that they are not equal.
  let notes = {};
  if (typeof(this.notes) != 'undefined') {
    Object.keys(this.notes).forEach(key => {
      if (key != note_id) {
        let note_object = this.notes[key];
        notes[note_object.id] = note_object;
      }
    });
  }

  this.notes = notes;

}

User.prototype.getSerializable = function () {
  return {
    avatar: this.avatar,
    coaching_service: this.coaching_service,
    days_of_training_planned: this.days_of_training_planned,
    first_name: this.first_name,
    id: this.id,
    last_name: this.last_name,
    next_race_days_until: this.next_race_days_until,
    next_race_name: this.next_race_name,
    next_race_path: this.next_race_path,
    unseen_changes_count: this.unseen_changes_count,
    unseen_changes_path: this.unseen_changes_path,
    unseen_messages_count: this.unseen_messages_count,
  }
}

User.prototype.getFullName = function () {
  var full_name = this.first_name + ' ' + this.last_name;
  full_name = full_name.trim();
  return (full_name == '') ? 'Name not set' : full_name;
}

User.prototype.getCoachCalendarPath = function () {
  return '/coach/' + this.id + '/';
}

User.prototype.getChartsPath = function () {
  return '/coach/' + this.id + '/charts/';
}

User.prototype.getPrivateMessagesPath = function () {
  return '/message/' + this.id + '/';
}

User.prototype.getPeriodisationPath = function () {
  return '/coach/' + this.id + '/periodisation/';
}

User.prototype.getCompareSessionsPath = function () {
  return '/coach/' + this.id + '/compare/';
}

User.prototype.getCompareSessionsNowPath = function (session_ids) {
  var session_ids = session_ids.slice();
  var main_session_id = session_ids.pop();
  var sessions_query_string = '?sessions=' + session_ids.join();
  return '/session/'+ main_session_id + '/' + sessions_query_string;
}

User.prototype.getCompareSessionsByTagPath = function (tags) {
  var tags = tags.slice();
  var main_tag = tags.pop();
  var sessions_query_string = '#tags=' + tags.join();
  return '/session/' + main_session_id + '/' + sessions_query_string;
}

User.prototype.getEditAccountPath = function (callback) {
  return '/accounts/' + this.id + '/edit/';
}

User.prototype.getEditAccountTypePath = function (callback) {
  return '/accounts/' + this.id + '/edit-account-type/';
}

User.prototype.getEditZonesPath = function (perspective) {
  if (perspective == 'coach') {
    return '/accounts/' + this.id + '/zones/';
  }
  else if (perspective == 'athlete') {
    return '/accounts/zones/';
  }
}

/**
 * Set the Beta access for this feature to true or false.
 */
User.prototype.setBetaAccess = function (feature_name, set_access_to, callback) {
  var self = this;

  var post_data = {}
  post_data.csrfmiddlewaretoken = getCookie('csrftoken');
  post_data.id = this.id;

  // Reverse whether it is active or not.
  this.beta_features[feature_name] = set_access_to;

  post_data.beta_features = self.beta_features;

  ajaxRequest({
    url: '/accounts/toggle-beta-access/',
    method: 'POST',
    data: post_data,
    success: callback,
    expected_response_type: 'json',
  });

}

/**
 * Bulk data has been uploaded from server database. Store it locally.
 */
var athletesLoaded = function (new_data) {

  var data_populated_datetime_string = new Date().toISOString();
  var new_table_metadata = {table_name: 'athletes', populated_datetime: data_populated_datetime_string };

  if (offlineModeIsActive()) {

    database.transaction("rw", database.athletes, database.table_metadata, function () {

      // Athletes may have been removed so get rid of these from the store and
      // the database.

      var redux_store_state = store.getState();
      var old_data_athlete_ids = Object.keys(redux_store_state.main.athletes);

      var new_data_athlete_ids = [];
      Object.keys(new_data.athletes).forEach(key => {
        new_data_athlete_ids.push(new_data.athletes[key].id);
      });

      var athlete_ids_to_delete = [];
      for (var i = 0; i < old_data_athlete_ids.length; i++) {
        let athlete_id = parseInt(old_data_athlete_ids[i]);
        if (new_data_athlete_ids.indexOf(athlete_id) === -1 && athlete_id != global_athlete_id_to_keep) {
          athlete_ids_to_delete.push(athlete_id);
        }
      }

      if (athlete_ids_to_delete.length > 0) {
        // Remove from the database.
        database.athletes.bulkDelete(athlete_ids_to_delete);

        // Remove from the Redux store.
        store.dispatch(Action.athleteRemove(athlete_ids_to_delete));
      }

      // Add the new data to the database and then send it to the Redux store.
      database.table_metadata.put(new_table_metadata).then(function () {
        database.athletes.bulkPut(new_data.athletes).then(function (last_key) {
          database.athletes.toArray(function (items) {
            store.dispatch(Action.athleteReceive(items));
            store.dispatch(Action.tableMetadataReceive(new_table_metadata));
          })
        });
      });

    }).catch(function (e) {
      store.dispatch(Action.athleteReceive(new_data.athletes));
      store.dispatch(Action.tableMetadataReceive(new_table_metadata));
    });
  }
  else {
    store.dispatch(Action.athleteReceive(new_data.athletes));
    store.dispatch(Action.tableMetadataReceive(new_table_metadata));
  }

}

const fetchAthletes = function () {
  ajaxRequest({
    url: '/accounts/load-athletes/',
    method: 'GET',
    success: athletesLoaded,
    expected_response_type: 'json',
  });
}

const populateAthleteData = function (athlete_id_to_keep) {

  // Register that we want to not delete a specific athlete. This is useful
  // for super users accessing athletes not on account.
  if (athlete_id_to_keep) {
    global_athlete_id_to_keep = athlete_id_to_keep;
  }

  // Attempt to load the data from Dexie.
  if (offlineModeIsActive()) {
    database.table_metadata.get('athletes', function (athletes_table) {

      var athletes_table = (athletes_table) ? athletes_table : {table_name: 'athletes', populated_datetime: null};

      database.athletes.toArray(function (items) {

        // Place these items into the Redux store.
        store.dispatch(Action.athleteReceive(items));

      }).then(function () {

        // Dispatch athlete table metadata to store.
        store.dispatch(Action.tableMetadataReceive(athletes_table));

        // Finished populating store with current database data. Now ajax in new data.
        fetchAthletes();

      }).catch(function (e) {
        fetchAthletes();
      });

    });

  }
  else {
    store.dispatch(Action.tableMetadataReceive({table_name: 'athletes', populated_datetime: null }));
    fetchAthletes();
  }

}

export default populateAthleteData
